/**
 * Organization Management Action Hooks
 *
 * These hooks provide high-level organization management actions
 * that convert Stytch responses to our domain types.
 */
import { discoveryOrganizationTimestamps } from './discovery-organization-timestamps';
import { useCallback } from 'react';
import { useStytchWrappers } from './stytch-hooks';
import { useStytchUpdateOrganization } from '../../../services/stytch/client/organization';
import { STYTCH_DEFAULT_SESSION_DURATION_MINUTES } from '../../../core/session-durations';
/**
 * Hook to exchange intermediate session for organization (minimal version)
 * This only handles the Stytch exchange - database operations should be handled separately
 */
export function useExchangeIntermediateSession() {
    const { exchangeIntermediateSession } = useStytchWrappers();
    return useCallback(async (organizationId, sessionDurationMinutes = STYTCH_DEFAULT_SESSION_DURATION_MINUTES) => {
        const result = await exchangeIntermediateSession(organizationId, sessionDurationMinutes);
        if (result.success) {
            return {
                success: true,
                memberId: result.memberId,
                sessionJwt: result.sessionJwt,
                sessionToken: result.sessionToken,
                member: result.member,
                organization: result.organization,
                memberAuthenticated: result.memberAuthenticated,
                primaryRequired: result.primaryRequired,
                mfaRequired: result.mfaRequired,
                data: result.data,
            };
        }
        else {
            return {
                success: false,
                error: result.error,
                data: result.data,
            };
        }
    }, []);
}
/**
 * Hook to join an existing organization
 */
export function useJoinOrganization() {
    const { exchangeIntermediateSession } = useStytchWrappers();
    return useCallback(async (organizationId, sessionDurationMinutes = STYTCH_DEFAULT_SESSION_DURATION_MINUTES) => {
        const result = await exchangeIntermediateSession(organizationId, sessionDurationMinutes);
        if (result.success) {
            return {
                success: true,
                requestId: result.requestId,
                statusCode: result.statusCode,
                memberId: result.memberId,
                sessionJwt: result.sessionJwt,
                sessionToken: result.sessionToken,
                intermediateSessionToken: result.intermediateSessionToken,
                member: result.member,
                memberSession: result.memberSession,
                organization: result.organization,
                memberAuthenticated: result.memberAuthenticated,
                primaryRequired: result.primaryRequired,
                mfaRequired: result.mfaRequired,
                data: result.data,
            };
        }
        else {
            return {
                success: false,
                error: result.error,
                data: result.data,
            };
        }
    }, []);
}
/**
 * Hook to get list of organizations user can join
 */
export function useGetOrganizations() {
    const { listDiscoveredOrganizations } = useStytchWrappers();
    return useCallback(async () => {
        const result = await listDiscoveredOrganizations();
        if (result.success) {
            return {
                success: true,
                emailAddress: result.email_address,
                organizations: result.discovered_organizations?.map(org => ({
                    id: '',
                    name: org.organization.organization_name,
                    domain: org.organization.organization_slug || '',
                    logoUrl: org.organization.organization_logo_url || null,
                    member_count: 0, // Would need to fetch this separately
                    ...discoveryOrganizationTimestamps(org.organization),
                    stytch_organization_id: org.organization.organization_id,
                    // Only 'active_member' orgs are switchable via session exchange;
                    // 'pending_member'/'invited_member' still need the invite-acceptance flow.
                    membershipType: org.membership?.type,
                    memberAuthenticated: org.member_authenticated,
                })),
                data: result.data,
            };
        }
        else {
            return {
                success: false,
                error: result.error,
                data: result.data,
            };
        }
    }, [listDiscoveredOrganizations]);
}
/**
 * Hook to switch the active session into a different organization the member
 * already belongs to (workspace switching). This exchanges the current
 * session for one scoped to the target organization without a full re-login.
 *
 * If the target organization requires additional authentication (e.g. MFA)
 * that the current session hasn't satisfied, `memberAuthenticated` will be
 * false and `mfaRequired`/`primaryRequired` describe what's needed — callers
 * should surface that instead of treating it as a hard failure.
 */
export function useSwitchOrganization() {
    const { exchangeSession } = useStytchWrappers();
    return useCallback(async (organizationId, sessionDurationMinutes = STYTCH_DEFAULT_SESSION_DURATION_MINUTES) => {
        const result = await exchangeSession(organizationId, sessionDurationMinutes);
        if (result.success) {
            return {
                success: true,
                sessionJwt: result.sessionJwt,
                sessionToken: result.sessionToken,
                member: result.member,
                organization: result.organization,
                memberAuthenticated: result.memberAuthenticated,
                primaryRequired: result.primaryRequired,
                mfaRequired: result.mfaRequired,
                data: result.data,
            };
        }
        else {
            return {
                success: false,
                error: result.error,
                data: result.data,
            };
        }
    }, [exchangeSession]);
}
/**
 * Hook to create a new organization
 */
export function useCreateOrganization() {
    const { createOrganizationViaDiscovery } = useStytchWrappers();
    return useCallback(async (organizationName, sessionDurationMinutes = STYTCH_DEFAULT_SESSION_DURATION_MINUTES, options) => {
        const result = await createOrganizationViaDiscovery(organizationName, sessionDurationMinutes, options);
        if (result.success) {
            return {
                success: true,
                memberId: result.data.member_id,
                sessionJwt: result.data.session_jwt,
                sessionToken: result.data.session_token,
                member: result.member,
                organization: result.organization,
                memberAuthenticated: result.data.member_authenticated,
                primaryRequired: result.data.primary_required,
                mfaRequired: result.data.mfa_required,
                data: result.data,
            };
        }
        else {
            return {
                success: false,
                error: result.error,
                data: result.data,
            };
        }
    }, [createOrganizationViaDiscovery]);
}
/**
 * Hook to update an organization
 */
export function useUpdateOrganization() {
    const updateOrganization = useStytchUpdateOrganization();
    return useCallback(async (data) => {
        const result = await updateOrganization(data);
        if (result.success) {
            return {
                success: true,
                requestId: result.requestId,
                statusCode: result.statusCode,
                organization: result.organization,
                data: result,
            };
        }
        else {
            return {
                success: false,
                error: result.error,
                data: result,
            };
        }
    }, []);
}
